import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface ELearningUser {
  first_name: string;
  last_name: string;
  hkms_hlk_number: string;
  email: string;
  institution: string;
}

export function ELearningPage() {
  const [user, setUser] = useState<ELearningUser | null>(null);
  const [registeredUser, setRegisteredUser] = useState<ELearningUser | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<ELearningUser>({
    first_name: '',
    last_name: '',
    hkms_hlk_number: '',
    email: '',
    institution: ''
  });

  // Check if user is already registered
  useEffect(() => {
    const savedEmail = localStorage.getItem('elearning_user_email');
    if (savedEmail) {
      checkUserRegistration(savedEmail);
    }
  }, []);

  const checkUserRegistration = async (email: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('elearning_users')
        .select('*')
        .eq('email', email)
        .single();
      
      if (error) {
        console.error('Error checking user:', error);
        setLoading(false);
        return;
      }
      
      if (data) {
        setRegisteredUser(data);
        localStorage.setItem('elearning_user_email', data.email);
        
        // Log this access in the access logs
        logUserAccess(data);
      }
    } catch (error) {
      console.error('Error checking user:', error);
    }
    setLoading(false);
  };

  // Log user access in the access logs
  const logUserAccess = async (userData: ELearningUser) => {
    try {
      const { error } = await supabase
        .from('elearning_access_logs')
        .insert([{
          email: userData.email,
          first_name: userData.first_name,
          last_name: userData.last_name,
          hkms_hlk_number: userData.hkms_hlk_number,
          institution: userData.institution
        }]);
        
      if (error) {
        console.error('Error logging user access:', error);
      }
    } catch (error) {
      console.error('Error logging user access:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.first_name || !formData.last_name || !formData.hkms_hlk_number || 
        !formData.email || !formData.institution) {
      toast.error('Molimo popunite sva polja');
      return;
    }
    
    setLoading(true);
    
    try {
      // Check if user already exists
      const { data: existingUser } = await supabase
        .from('elearning_users')
        .select('*')
        .eq('email', formData.email)
        .single();
      
      if (existingUser) {
        // User exists, just set them as registered and log access
        setRegisteredUser(existingUser);
        localStorage.setItem('elearning_user_email', existingUser.email);
        
        // Log this access in the access logs
        logUserAccess(existingUser);
        
        toast.success('Uspješno ste se prijavili');
        setLoading(false);
        return;
      }
      
      // Insert new user
      const { data, error } = await supabase
        .from('elearning_users')
        .insert([formData])
        .select()
        .single();
      
      if (error) {
        toast.error('Greška prilikom registracije. Pokušajte ponovno.');
        console.error('Error registering user:', error);
        setLoading(false);
        return;
      }
      
      // Set user as registered
      setRegisteredUser(data);
      localStorage.setItem('elearning_user_email', data.email);
      
      // Log this access in the access logs
      logUserAccess(data);
      
      toast.success('Uspješno ste se registrirali');
    } catch (error) {
      console.error('Error registering user:', error);
      toast.error('Greška prilikom registracije. Pokušajte ponovno.');
    }
    
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('elearning_user_email');
    setRegisteredUser(null);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">E-Learning</h1>
      
      {registeredUser ? (
        // Content for registered users
        <div>
          <div className="bg-white shadow-md rounded-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-700">Dobrodošli, {registeredUser.first_name} {registeredUser.last_name}</h2>
              <button 
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
              >
                Odjava
              </button>
            </div>
            <p className="text-gray-600 mb-4">
              Pristupite edukativnim video materijalima za onkološke medicinske sestre i tehničare.
            </p>
          </div>

          {/* Video content */}
          <div className="mb-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Video lekcije</h3>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* 1. Ivona Poljak */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="relative pb-[56.25%] h-0 overflow-hidden">
                <iframe 
                  src="https://www.youtube.com/embed/24rQ1FZNv3I" 
                  title="Izazovi komunikacije u onkologiji: kako očuvati pacijenta i sebe?" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Izazovi komunikacije u onkologiji: kako očuvati pacijenta i sebe?
                </h3>
                <p className="text-gray-600 mb-4">Predavač: Ivona Poljak, psihologinja KBC Zagreb</p>
              </div>
            </div>

            {/* 2. Preventivni programi */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="relative pb-[56.25%] h-0 overflow-hidden">
                <iframe 
                  src="https://www.youtube.com/embed/hyfjBgqizI0" 
                  title="Preventivni programi u Republici Hrvatskoj" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Preventivni programi u Republici Hrvatskoj</h3>
                <p className="text-gray-600 mb-4">Predavač: Kralj-Škoc Vesna</p>
              </div>
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="relative pb-[56.25%] h-0 overflow-hidden">
                <iframe 
                  src="https://www.youtube.com/embed/EOSK8fU6vfI" 
                  title="Karcinogenza" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Karcinogenza</h3>
                <p className="text-gray-600 mb-4">Predavač: dr.med, univ.mag.med. Domina Kekez</p>
              </div>
            </div>

            {/* 4. Sustavno liječenje tumora */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="relative pb-[56.25%] h-0 overflow-hidden">
                <iframe 
                  src="https://www.youtube.com/embed/XydsjRGa9Sw" 
                  title="Osnove sustavnog liječenja tumora" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Osnove sustavnog liječenja tumora</h3>
                <p className="text-gray-600 mb-4">Predavač: doc. dr. sc.Juraj Prejac, dr. med.</p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Registration form for non-registered users
        <div className="max-w-2xl mx-auto">
          <div className="bg-white shadow-md rounded-lg p-6 mb-8">
            <h2 className="text-2xl font-semibold text-gray-700 mb-6">Registracija za E-Learning</h2>
            <p className="text-gray-600 mb-6">
              Za pristup edukativnim materijalima potrebno je registrirati se. Molimo popunite sljedeći obrazac:
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Ime
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Prezime
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="hkms_hlk_number" className="block text-sm font-medium text-gray-700 mb-1">
                    HKMS/HLK broj
                  </label>
                  <input
                    type="text"
                    id="hkms_hlk_number"
                    name="hkms_hlk_number"
                    value={formData.hkms_hlk_number}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1">
                    Institucija
                  </label>
                  <input
                    type="text"
                    id="institution"
                    name="institution"
                    value={formData.institution}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="mt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-300"
                  >
                    {loading ? 'Učitavanje...' : 'Registriraj se'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 